import { useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { formatDate } from '@/lib/utils/date';
import { useCampaignReportDownload, useHistoricalCampaigns } from '../api';
import { buildCampaignColumns, DEFAULT_VISIBLE_COLUMNS } from '../columns';
import {
  CampaignFilters,
  applyCampaignFilters,
  rowsHaveRetries,
} from '../components/CampaignFilters';
import type { CampaignRow } from '../types';

export function VoiceCampaignHistoricalPage() {
  const userId = useAuthStore((s) => s.userId);
  const search = useHistoricalCampaigns();
  const allRows = search.data ?? [];

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [hasSearched, setHasSearched] = useState(false);

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const rows = useMemo(
    () => applyCampaignFilters(allRows, selectedStatuses, selectedTemplates),
    [allRows, selectedStatuses, selectedTemplates],
  );

  const hasRetries = rowsHaveRetries(rows);
  const baseTotal = useMemo(
    () => rows.reduce((acc, r) => acc + Number(r.numbersUploaded ?? 0), 0),
    [rows],
  );

  // Historical campaigns still need the row-actions menu — users open
  // it to generate full / webhook reports, retry the campaign, or fire
  // webhook-retry actions. Each menu item is internally status-gated.
  const reportDownload = useCampaignReportDownload();
  const columns = useMemo(
    () =>
      buildCampaignColumns({
        onDownloadReport: reportDownload.download,
        baseTotal,
      }),
    [reportDownload.download, baseTotal],
  );

  const visibility = {
    ...DEFAULT_VISIBLE_COLUMNS,
    retryCount: hasRetries,
    retries: hasRetries,
  };

  const handleSearch = () => {
    if (startDate && endDate && startDate > endDate) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    setHasSearched(true);
    search.mutate(
      {
        userId: Number(userId),
        startDate: startDate ? formatDate(startDate) : '',
        endDate: endDate ? formatDate(endDate) : '',
      },
      {
        onError: () => toast.error('Could not fetch historical campaigns.'),
      },
    );
  };

  return (
    <FillPage>
      <PageHeader
        title="Campaign History"
        description="Look up completed campaigns by date range."
      />

      {/*
        Inline filter strip — the old Card-wrapped variant burned a lot of
        vertical space above an otherwise-empty table. Single row with a
        muted hint on the right keeps the date inputs visible without
        crowding the page.
      */}
      <div className="flex flex-col gap-2 rounded-lg border bg-card p-3 sm:flex-row sm:items-center">
        <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-center">
          <DatePicker
            value={startDate}
            onChange={setStartDate}
            placeholder="Start date"
          />
          <DatePicker
            value={endDate}
            onChange={setEndDate}
            placeholder="End date"
          />
          <Button onClick={handleSearch} disabled={search.isPending}>
            {search.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Search
          </Button>
        </div>
        <span className="text-[11px] text-muted-foreground sm:ml-3 sm:whitespace-nowrap">
          Leave dates empty to fetch the full history.
        </span>
      </div>

      <DataTable<CampaignRow, unknown>
        tableId="campaigns:voice:historical:v1"
        columns={columns}
        data={rows}
        isLoading={search.isPending}
        searchPlaceholder="Filter results…"
        emptyTitle={hasSearched ? 'No campaigns found' : 'Pick a range to begin'}
        emptyDescription={
          hasSearched
            ? 'Try widening the date range.'
            : 'Choose start / end dates above and click Search.'
        }
        getRowId={(row) => String(row.campaignId)}
        initialColumnVisibility={visibility}
        exportFilename="voice-campaigns-historical.csv"
        pageSize={10}
        toolbar={
          <CampaignFilters
            selectedStatuses={selectedStatuses}
            selectedTemplates={selectedTemplates}
            onStatusChange={setSelectedStatuses}
            onTemplateChange={setSelectedTemplates}
          />
        }
      />
    </FillPage>
  );
}
